import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Steps, Button, message, Spin, Tag } from "antd";
import { DEFAULT_SETUP_STATE, SetupState, SplitOption } from "./mockData";
import SetupStep from "./SetupStep";
import ResultsStep from "./ResultsStep";
import {
  createRctExperimentJob,
  fetchJobRunProgress,
  fetchJobRunStatus,
  fetchDesignResult,
  fetchRctExperimentConfig,
  DesignResult,
  JobRunProgress,
} from "./rctExperimentApi";

const STEPS = [
  { id: "setup", title: "Configure Experiment" },
  { id: "results", title: "Results" },
];

// The design job can take tens of minutes - market pairing and the full
// trial batch are re-run for every candidate duration within each split
// (validated at ~20 min for one split across 3 durations at 5000 trials
// each) - so this polls rather than waiting on a single request.
const POLL_INTERVAL_MS = 4000;

const RctExperimentStudy: React.FC = () => {
  const navigate = useNavigate();
  const { id: jobConfigId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("view") === "true";

  const [current, setCurrent] = useState(0);
  const [setup, setSetup] = useState<SetupState>(DEFAULT_SETUP_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<JobRunProgress | null>(null);
  const [designResult, setDesignResult] = useState<DesignResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(isViewMode);
  const [viewError, setViewError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  // View mode - navigated here from the Past Tests list's "View" button
  // (only shown for already-SUCCESSFUL jobs), so no upload/run needed: just
  // load the existing config + results and jump straight to Results.
  useEffect(() => {
    if (!isViewMode || !jobConfigId) return;
    let cancelled = false;
    (async () => {
      try {
        const [config, result] = await Promise.all([
          fetchRctExperimentConfig(jobConfigId),
          fetchDesignResult(jobConfigId),
        ]);
        if (cancelled) return;
        if (config) {
          setSetup((prev) => ({
            ...prev,
            studyName: config.name,
            durations: config.durations,
            splits: config.splits as SplitOption[],
            totalWeeklySpend: config.totalWeeklySpend,
            weeklyCap: config.weeklyCap,
          }));
        }
        if (!result) {
          setViewError("No results were found for this experiment.");
          return;
        }
        setDesignResult(result);
        setCurrent(1);
      } catch (err) {
        console.error("[RctExperiment] Failed to load experiment for viewing:", err);
        if (!cancelled) setViewError("Failed to load this experiment. Please try again.");
      } finally {
        if (!cancelled) setViewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isViewMode, jobConfigId]);

  const startPolling = (runId: string) => {
    stopPolling();

    const poll = async () => {
      try {
        const [latestProgress, status] = await Promise.all([
          fetchJobRunProgress(runId),
          fetchJobRunStatus(runId),
        ]);
        if (latestProgress) setProgress(latestProgress);

        if (status === "SUCCESSFUL" || latestProgress?.phase === "completed") {
          stopPolling();
          const result = await fetchDesignResult(jobConfigId!);
          setSubmitting(false);
          if (!result) {
            setRunError("The design job finished but no results were found. Please go back and try again.");
            return;
          }
          setDesignResult(result);
        } else if (status === "FAILED" || latestProgress?.phase === "failed") {
          stopPolling();
          setSubmitting(false);
          setRunError(
            latestProgress?.message ||
              "The experiment design job failed. Please check your data and try again."
          );
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          // Job run not found - stop polling instead of retrying forever.
          stopPolling();
          setSubmitting(false);
          setRunError("Lost track of this experiment run. Please go back and try again.");
          return;
        }
        // Any other error is treated as transient - keep the interval alive
        // rather than failing the whole run over one dropped request.
        console.error("[RctExperiment] progress poll failed:", err);
      }
    };

    poll();
    pollTimer.current = setInterval(poll, POLL_INTERVAL_MS);
  };

  const patchSetup = (patch: Partial<SetupState>) => setSetup((prev) => ({ ...prev, ...patch }));

  const isSetupComplete =
    setup.studyName.trim() !== "" &&
    setup.uploaded &&
    setup.durations.length > 0 &&
    setup.splits.length > 0 &&
    !!setup.weeklyCap &&
    setup.weeklyCap > 0 &&
    !!setup.totalWeeklySpend &&
    setup.totalWeeklySpend > 0;

  // A completed run must not be resubmitted by stepping back to Setup and forward again.
  const hasCompletedRun = !!designResult;

  const isNextDisabled = () => {
    if (isViewMode || hasCompletedRun) return false;
    if (STEPS[current].id === "setup") return !isSetupComplete || submitting;
    return false;
  };

  const handleNext = async () => {
    if (STEPS[current].id === "setup") {
      // View mode, or a run already completed this session - nothing to
      // (re-)submit, just step forward.
      if (isViewMode || hasCompletedRun) {
        setCurrent(current + 1);
        return;
      }
      if (!jobConfigId) return;

      setSubmitting(true);
      setRunError(null);
      setDesignResult(null);
      setProgress(null);
      setCurrent(current + 1);

      try {
        const { jobRunId } = await createRctExperimentJob(jobConfigId, setup);
        if (!jobRunId) {
          throw new Error("Job run was created but no jobRunId was returned");
        }
        startPolling(jobRunId);
      } catch (err) {
        console.error("[RctExperiment] Failed to start design job:", err);
        setSubmitting(false);
        setRunError("Failed to upload data and start the experiment. Please go back and try again.");
        message.error("Failed to start the experiment design job.");
      }
      return;
    }
    if (current < STEPS.length - 1) setCurrent(current + 1);
  };

  const handlePrev = () => {
    setCurrent((c) => Math.max(c - 1, 0));
  };

  if (isViewMode && viewLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spin size="large" tip="Loading experiment…" />
      </div>
    );
  }

  if (isViewMode && viewError) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{viewError}</p>
          <Button onClick={() => navigate("/rct-experiment/list")}>Back to Past Tests</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Stepper Section */}
        <div className="pr-4 ml-6 overflow-y-auto" style={{ width: "25%", flexShrink: 0, height: "100%" }}>
          <h2 className="text-2xl font-semibold mt-5 mb-2 text-gray-900">
            {setup.studyName || "New RCT Experiment"}
          </h2>
          {isViewMode && (
            <Tag color="blue" className="mb-4">
              Viewing
            </Tag>
          )}

          <Steps
            direction="vertical"
            current={current}
            items={STEPS.map((step) => ({
              title: step.title,
              disabled: true,
            }))}
            className="[&_.ant-steps-item]:mb-12 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[100px] [&_.ant-steps-item-tail]:!w-[3px] [&_.ant-steps-item-icon]:!text-lg [&_.ant-steps-item-icon]:!w-8 [&_.ant-steps-item-icon]:!h-8 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-base"
          />
        </div>

        {/* Content Section */}
        <div
          style={{
            flex: 1,
            borderLeft: "1px solid #f0f0f0",
            overflow: "auto",
            padding: "24px 16px",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ paddingBottom: 72 }}>
            {STEPS[current].id === "setup" && (
              <SetupStep setup={setup} onChange={patchSetup} readOnly={isViewMode} />
            )}
            {STEPS[current].id === "results" && jobConfigId && (
              <ResultsStep
                jobConfigId={jobConfigId}
                setup={setup}
                designResult={designResult}
                progress={progress}
                runError={runError}
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer — in-flow so it doesn't cover content */}
      <div className="bg-white border-t border-gray-200 p-4 flex justify-between" style={{ flexShrink: 0 }}>
        <div>
          {current > 0 && (
            <Button
              onClick={handlePrev}
              size="large"
              className="mr-2"
              icon={<span className="mr-1">←</span>}
              disabled={submitting}
            >
              Previous
            </Button>
          )}
        </div>

        <div>
          {STEPS[current].id === "setup" && (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              disabled={isNextDisabled()}
              icon={<span className="ml-1">→</span>}
            >
              {isViewMode || hasCompletedRun ? "Next" : "Run Experiment"}
            </Button>
          )}
          {STEPS[current].id === "results" && designResult && (
            <Button type="primary" size="large" onClick={() => navigate("/rct-experiment/list")}>
              {isViewMode ? "Back to Past Tests" : "Finish"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RctExperimentStudy;
